import { useEffect } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import SiteFooter from "@/components/SiteFooter";
import {
  Sparkles,
  Search,
  Bot,
  Compass,
  TrendingUp,
  Target,
  CheckCircle2,
  ArrowRight,
  HelpCircle,
  MapPin,
  Rocket,
} from "lucide-react";

const PAGE_TITLE =
  "What Is GEO? Generative Engine Optimization Explained | uSeen";
const PAGE_DESC =
  "GEO helps businesses become more visible in AI-powered answer engines such as ChatGPT, Gemini, Google AI and Perplexity.";

const FAQ_ITEMS = [
  {
    q: "What is GEO?",
    a: "GEO stands for Generative Engine Optimization. It refers to the work done to be more visible and more recommendable inside AI-powered answer engines.",
  },
  {
    q: "Is GEO the same as SEO?",
    a: "No. SEO focuses on ranking higher in traditional search engines like Google. GEO focuses on appearing inside the answers given by ChatGPT, Gemini, Google AI and similar AI systems.",
  },
  {
    q: "Who is GEO important for?",
    a: "It matters for local businesses, service providers, restaurants, clinics, hotels, beauty centres, e-commerce brands and any business that wants to stay visible online.",
  },
  {
    q: "How is GEO work done?",
    a: "Your website, business information, Google Business Profile, customer reviews, content, social presence and third-party brand visibility are all analysed and improved together.",
  },
  {
    q: "What does uSeen do for GEO?",
    a: "uSeen analyses how visible your business is in AI tools, surfaces the gaps, compares you to competitors and produces actionable tasks to lift visibility.",
  },
  {
    q: "Is GEO only for large brands?",
    a: "No. GEO is becoming increasingly important for local businesses too — because users now ask AI directly for things like 'the best business near me' or 'a trusted provider in this area'.",
  },
];

/**
 * Public SEO landing page — /en/what-is-geo
 * Mirrors /geo-nedir for English visitors.
 */
export default function GeoEn() {
  useEffect(() => {
    const prevTitle = document.title;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    document.title = PAGE_TITLE;
    if (metaDesc) metaDesc.setAttribute("content", PAGE_DESC);

    const tags = [
      ["property", "og:title", PAGE_TITLE],
      ["property", "og:description", PAGE_DESC],
      ["property", "og:type", "article"],
      ["name", "twitter:title", PAGE_TITLE],
      ["name", "twitter:description", PAGE_DESC],
    ];
    const added = [];
    for (const [attr, key, val] of tags) {
      let tag = document.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
        added.push(tag);
      }
      tag.setAttribute("content", val);
    }

    const ORIGIN = "https://useen.ai";
    const alternates = [
      { hreflang: "en", href: `${ORIGIN}/en/what-is-geo` },
      { hreflang: "tr", href: `${ORIGIN}/geo-nedir` },
      { hreflang: "x-default", href: `${ORIGIN}/en/what-is-geo` },
    ];
    document.querySelectorAll('link[rel="alternate"][data-useen-hreflang]').forEach((el) => el.remove());
    const addedLinks = [];
    alternates.forEach(({ hreflang, href }) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", hreflang);
      link.setAttribute("href", href);
      link.setAttribute("data-useen-hreflang", "1");
      document.head.appendChild(link);
      addedLinks.push(link);
    });

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-useen-faq", "geo-en");
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(ld);

    const prevHtmlLang = document.documentElement.lang;
    document.documentElement.lang = "en";

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== undefined && prevDesc !== null) {
        metaDesc.setAttribute("content", prevDesc);
      }
      added.forEach((t) => t.parentNode?.removeChild(t));
      addedLinks.forEach((l) => l.parentNode?.removeChild(l));
      ld.parentNode?.removeChild(ld);
      document.documentElement.lang = prevHtmlLang;
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-slate-200 flex flex-col" data-testid="geo-en-page">
      <div className="absolute inset-0 bg-grid opacity-[0.08] pointer-events-none" />

      <header className="relative z-10 px-6 lg:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center"><Logo /></Link>
        <nav className="flex items-center gap-5 text-sm">
          <Link to="/fiyatlandirma" className="text-slate-300 hover:text-cyan-300">Pricing</Link>
          <Link to="/giris" className="text-slate-300 hover:text-cyan-300">Log in</Link>
          <Link to="/kayit" className="neon-btn rounded-lg px-4 py-2 text-white text-sm font-medium" data-testid="header-cta-register">
            Start free
          </Link>
        </nav>
      </header>

      <main className="relative z-10 flex-1 px-6 lg:px-12 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-300 mb-3 inline-flex items-center gap-1.5">
            <Sparkles size={11} /> GEO · Guide
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6">
            What Is GEO?
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed mb-3">
            <strong className="text-white">GEO (Generative Engine Optimization)</strong> is a new optimization
            approach that aims to make brands visible inside
            <strong className="text-white"> ChatGPT, Gemini, Google AI, Perplexity </strong>
            and other AI-powered answer engines.
          </p>
          <p className="text-slate-400 leading-relaxed mb-6">
            SEO got businesses to the top of Google. GEO is about being shown, named and recommended inside
            the answers given by AI assistants.
          </p>

          <ExampleAnswers />

          <p className="text-slate-300 leading-relaxed mt-6 mb-12">
            Whether your business is named in those answers is determined by your GEO posture.
            <strong className="text-white"> That is exactly what GEO optimises.</strong>
          </p>

          <Article>
            <Section icon={<TrendingUp size={18} />} title="Why GEO Matters">
              <p>
                Information habits are shifting. Instead of scanning ten search results, users ask AI tools for direct,
                summarised recommendations.
              </p>
              <p>
                For businesses this means a new reality: ranking on Google is no longer enough. AI engines must also be
                able to understand you, trust you and recommend you.
              </p>
              <p>
                Your AI visibility is shaped by your website, Google Business Profile, customer reviews, social presence,
                third-party mentions and the overall consistency of your brand information online.
              </p>
            </Section>

            <Section icon={<Compass size={18} />} title="What Does GEO Cover?">
              <p>
                GEO is broader than classic on-page SEO. It is a complete digital visibility programme.
              </p>
              <BulletList items={[
                "Making your website readable and summarisable for AI tools",
                "Keeping your business information consistent across the web",
                "Maintaining an accurate, current Google Business Profile",
                "Cultivating trustworthy customer reviews",
                "Being mentioned across credible third-party sources",
                "Clearly describing your services and core strengths",
                "Owning content that answers the real customer questions in your category",
                "Helping AI tools categorise your business correctly",
              ]} />
              <p>
                In short, GEO makes your business
                <strong className="text-white"> discoverable, understandable and recommendable </strong>
                inside generative answer engines.
              </p>
            </Section>

            <Section icon={<Search size={18} />} title="SEO vs. GEO">
              <p>
                <strong className="text-white">SEO</strong> aims to rank your URL on classic search engines like Google.
              </p>
              <p>
                <strong className="text-white">GEO</strong> aims to get your brand name and offering into the AI-generated
                answer itself.
              </p>
              <div className="not-prose grid sm:grid-cols-2 gap-3 my-5">
                <ComparisonCard
                  badge="SEO"
                  badgeColor="text-slate-300 border-slate-500/30 bg-slate-500/10"
                  title="Top 10 on Google"
                  desc="Wins by ranking your URL in classic search."
                />
                <ComparisonCard
                  badge="GEO"
                  badgeColor="text-cyan-300 border-cyan-500/30 bg-cyan-500/10"
                  title="Inside the AI answer"
                  desc="Wins when AI mentions your business by name in its response."
                />
              </div>
              <p>SEO still matters. GEO is what you build on top of it for the AI era.</p>
            </Section>

            <Section icon={<Bot size={18} />} title="How Do AI Engines Choose Who to Recommend?">
              <p>
                AI engines never rely on a single source. They aggregate many signals across the web. For a local business,
                the most important typically include:
              </p>
              <BulletList items={[
                "A clear website with services, location and contact details",
                "Strong, fresh customer reviews",
                "Consistent NAP information (name, address, phone) everywhere",
                "An active and authentic social media presence",
                "Real, positive mentions by customers and third parties",
                "A clear answer to the question: \"what is this business best known for?\"",
              ]} />
            </Section>

            <Section icon={<MapPin size={18} />} title="GEO for Local Businesses">
              <p>
                {"Local businesses benefit the most from GEO. Users increasingly ask AI direct, local questions — \"the best clinic in this district\", \"a trustworthy auto service near me\", \"a good restaurant in this neighbourhood\"."}
              </p>
              <p>
                The businesses that win on GEO are the ones whose name appears inside those AI answers.
              </p>
            </Section>

            <Section icon={<Target size={18} />} title="Where Does uSeen Fit In?">
              <p>
                <strong className="text-white">uSeen</strong> is a GEO platform. It analyses how visible your business
                is inside AI engines, compares you to competitors, identifies the gaps and creates a clear action plan.
              </p>
              <BulletList items={[
                "How visible your business is inside AI tools",
                "Whether competitors outperform you on AI visibility",
                "Which of your details are missing or inconsistent",
                "Whether your website is being understood by AI",
                "Which customer intents you are weak on",
                "Which content would lift visibility the most",
                "Which actions should be prioritised first",
              ]} />
            </Section>

            <Section icon={<CheckCircle2 size={18} />} title="Who Is GEO For?">
              <p>GEO is becoming critical for these business types in particular:</p>
              <div className="not-prose grid grid-cols-2 sm:grid-cols-3 gap-2 my-4">
                {[
                  "Restaurants", "Cafés", "Clinics",
                  "Beauty centres", "Hotels", "Camp sites",
                  "Auto services", "Real-estate offices", "Private schools",
                  "Health & care", "Local services", "Other brands",
                ].map((label) => (
                  <div key={label} className="rounded-lg border border-white/5 bg-black/30 px-3 py-2 text-sm text-slate-300">
                    {label}
                  </div>
                ))}
              </div>
            </Section>

            <Section icon={<Rocket size={18} />} title="The Bottom Line">
              <p>
                <strong className="text-white">GEO is the next evolution of digital visibility.</strong>
              </p>
              <p>
                Brands that take GEO seriously will be the ones AI tools mention by name when users ask for recommendations.
              </p>
            </Section>
          </Article>

          <div
            className="mt-12 rounded-3xl border border-cyan-500/20 bg-cyan-500/5 p-7 sm:p-10 relative overflow-hidden"
            data-testid="geo-en-cta-block"
          >
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-cyan-500/15 blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
                Start your GEO journey with uSeen
              </h2>
              <p className="text-slate-300 leading-relaxed max-w-2xl mb-6">
                See where your business stands today in AI engines, and what to fix first to get recommended.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/kayit" className="neon-btn rounded-xl px-5 py-3 text-white font-semibold inline-flex items-center gap-2" data-testid="geo-en-cta-primary">
                  Start free <ArrowRight size={14} />
                </Link>
                <Link to="/fiyatlandirma" className="ghost-btn rounded-xl px-5 py-3 text-white border border-white/10 inline-flex items-center gap-2" data-testid="geo-en-cta-secondary">
                  See plans
                </Link>
              </div>
              <div className="mt-6 text-sm text-slate-400">
                Keep reading: <Link to="/en/what-is-ai-visibility" className="text-cyan-300 hover:text-cyan-200" data-testid="geo-en-internal-aiv">What is AI Visibility?</Link>
              </div>
            </div>
          </div>

          <section className="mt-16" data-testid="geo-en-faq">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-purple-400 mb-3">
              <HelpCircle size={11} /> Frequently asked questions
            </div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mb-6">Common questions about GEO</h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map((f, i) => (
                <details
                  key={i}
                  className="group rounded-2xl border border-white/5 bg-black/30 px-5 py-4 open:bg-white/[0.03] transition-colors"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 text-white font-medium">
                    <span>{f.q}</span>
                    <span className="text-cyan-300 text-xl leading-none group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="text-slate-300 leading-relaxed mt-3">{f.a}</p>
                </details>
              ))}
            </div>
            <div className="mt-8 text-sm text-slate-400 text-center">
              Got more questions?{" "}
              <Link to="/iletisim" className="text-cyan-300 hover:text-cyan-200" data-testid="geo-en-contact-link">Get in touch</Link>.
            </div>
          </section>
        </div>
      </main>

      <SiteFooter lang="en" />
    </div>
  );
}

function Article({ children }) {
  return <article className="space-y-10 text-slate-300 leading-relaxed">{children}</article>;
}

function Section({ icon, title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl sm:text-[1.7rem] font-bold text-white mb-3 flex items-center gap-2.5">
        {icon && <span className="text-cyan-300">{icon}</span>}
        {title}
      </h2>
      <div className="space-y-3 text-[0.975rem]">{children}</div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-1.5 my-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-cyan-400 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function ComparisonCard({ badge, badgeColor, title, desc }) {
  return (
    <div className="rounded-xl border border-white/5 bg-black/30 p-4">
      <div className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${badgeColor} mb-2`}>{badge}</div>
      <div className="text-white text-sm font-medium mb-1">{title}</div>
      <div className="text-xs text-slate-400 leading-relaxed">{desc}</div>
    </div>
  );
}

function ExampleAnswers() {
  const examples = [
    "A few well-reviewed restaurants near you that are open now…",
    "Trusted dental clinics in your district based on recent reviews…",
    "Camp sites in your region with strong nature ratings…",
    "Reliable auto-service providers in your area…",
  ];
  return (
    <div className="rounded-2xl border border-white/5 bg-black/30 p-5">
      <div className="text-[11px] uppercase tracking-[0.15em] text-slate-400 mb-3 flex items-center gap-1.5">
        <Bot size={11} /> Example AI answers that mention businesses
      </div>
      <ul className="space-y-2">
        {examples.map((q, i) => (
          <li key={i} className="text-slate-300 text-[0.95rem] flex items-start gap-2.5">
            <span className="text-cyan-400 shrink-0">›</span>
            <span>{`"${q}"`}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
